import React, { useEffect, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { FormattedMessage } from "react-intl";
import { useQuery } from "react-query";
import { NavLink } from "react-router-dom";
import { BetaChip } from "../components/BetaChip";
import { MenuItemLink } from "../Layout/Menu/Menu";
import { Column, Row } from "../Layout/utils";
import { checkErrors, getDefaultOptions } from "../services/Category";
import { ConfigurationPage, ConfigurationSectionType } from "./interfaces";
import { useRecoilValue } from "recoil";
import { currentUserAtom, selectedCustomerAtom, User } from "../AuthProvider";
import { triggerUnmappeQueryAtom } from "./atoms";
import { isRequireFlagAccess } from "./utils";

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: "0px 40px 0 10px",
    flex: 0,
    borderLeft: `solid 2px ${theme.vars.palette.divider}`,
  },
  label: {
    width: "160px",
    fontSize: "0.875rem",
  },
  menuTitle: {
    color: theme.vars.palette.text.primary,
    margin: 0,
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  link: {
    color: theme.vars.palette.text.primary,
    justifyContent: "space-between",
    "&.active": {
      color: theme.vars.palette.text.accent,
    },
    "&:hover": {
      color: theme.vars.palette.text.accent,
    },
    "&.disabled": {
      color: theme.vars.palette.text.disabled,

      "&:hover:": {
        color: theme.vars.palette.text.disabled,
      },
    },
  },
  chip: {
    height: "20px",
    backgroundColor: theme.vars.palette.error.main,
    color: theme.vars.palette.common.white,
  },
}));

export const displayCheck = (
  page: ConfigurationPage,
  user: User | null,
  selectedCustomer: string | null = null
): boolean => {
  const flagCheck =
    !page.flagDisabled || user?.tenant?.flags[page.flagDisabled] === "1";

  const staffCheck = !page.isStaff || !!user?.is_staff;

  const customerCheck =
    !page.includedCustomers ||
    page.includedCustomers.includes(selectedCustomer ?? "");

  return flagCheck && staffCheck && customerCheck;
};

export const getUnmapped = async () => {
  const url = new URL("/api/unmapped/", window.origin);

  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);

  return response.json();
};

const PageLink = ({
  page,
  section,
  currentUser,
}: {
  page: ConfigurationPage;
  section: ConfigurationSectionType;
  currentUser: User | null;
}) => {
  const { classes } = useStyles();
  const reloadUnmappedAtom = useRecoilValue(triggerUnmappeQueryAtom);
  const [count, setCount] = useState<number>(0);
  const path = `/configuration/${section.path}/${page.path}`;
  const query = useQuery(["/api/unmapped/", reloadUnmappedAtom], () =>
    getUnmapped()
  );
  const flagDisabled = !isRequireFlagAccess(page, currentUser);

  useEffect(() => {
    if (query.data && page.name) {
      setCount(query.data[page.name]);
    }
  }, [page.name, query.data]);

  return (
    <MenuItemLink
      className={classes.link + (flagDisabled ? " disabled" : "")}
      key={path}
      component={NavLink}
      to={flagDisabled ? "#" : path} // we disable the link by removing the destination, otherwise, we are redirected to default page disabling onClick might be better but I didn't found how
      sx={{ py: 0.375 }}
    >
      <Typography component="span" className={classes.label}>
        <FormattedMessage id={page.menuLabel} />
      </Typography>
      {/* Dense, like the one the navigation submenu shows: this is a menu row, and
          the full-size chip would push it taller than its neighbours. */}
      {page.isBeta && <BetaChip dense />}
      {!!count && <Chip size="small" className={classes.chip} label={count} />}
    </MenuItemLink>
  );
};

const SectionsMenu = ({
  sections,
}: {
  sections: ConfigurationSectionType[];
}) => {
  const { classes } = useStyles();
  const currentUser = useRecoilValue(currentUserAtom);
  const selectedCustomer = useRecoilValue(selectedCustomerAtom);

  return (
    <Row>
      {sections.map((section) => (
        <Column
          key={section.path}
          className={classes.root}
          sx={{
            gap: 1,
          }}
        >
          <Typography component="p" className={classes.menuTitle}>
            <FormattedMessage id={section.label} />
          </Typography>
          <Box>
            {section.pages
              .filter((page) =>
                displayCheck(page, currentUser, selectedCustomer)
              )
              .map((page) => (
                <PageLink
                  key={section.path + page.path}
                  page={page}
                  section={section}
                  currentUser={currentUser}
                />
              ))}
          </Box>
        </Column>
      ))}
    </Row>
  );
};

export default SectionsMenu;
